import { SectionCard } from '@atlas/ui-components'
import type { ChangeRequest, ValidationLane } from '@/domain/workflow.types'
import { VALIDATION_LANE_LABEL } from '@/domain/workflow-stub.seed'
import { useToggleChangeRequestItem } from '@/hooks/useChangeRequests'
import { useCan } from '@/lib/capabilities'
import { REFUSAL } from '@/lib/refusal'
import { ChangeRequestCard } from './ChangeRequestCard'

const HANDED_OVER = 'Handed over for validation — these ticks are locked until the order is sent back.'

/**
 * Read-mostly surfacing of a lane's change requests wherever the underlying
 * work happens (e.g. the production workspace): items are ticked done here —
 * a tick the reviewer's verification in the validation workspace never reads —
 * but raising a new request or removing one only happens where it was raised
 * (the validation workspace's `ChangeRequestAuthor`), so this view passes
 * neither removal callback and therefore renders neither control.
 *
 * Every request for the lane renders, resolved ones included and marked as
 * such. Only a lane that has never had one renders nothing — a request that
 * exists is a record, and checking its last item must not erase it.
 *
 * Ticking is work, so a read-only role sees the ticks refused.
 *
 * Done ticks only change while the order sits where the request sent it back:
 * once handed over for validation, what the reviewer checks is frozen.
 */
export function ChangeRequestToggleList({
	workOrderId,
	lane,
	changeRequests,
	currentStatus,
}: {
	workOrderId: string
	lane: ValidationLane
	/** The lane's own requests. */
	changeRequests: readonly ChangeRequest[]
	/** The order's real current status code; `undefined` while it loads. */
	currentStatus: string | undefined
}) {
	const toggleItem = useToggleChangeRequestItem(workOrderId)
	const { canWork } = useCan()
	if (!changeRequests.length) return null

	return (
		<SectionCard
			title={`Change requests · ${VALIDATION_LANE_LABEL[lane]}`}
			count={changeRequests.length}
			className="bg-ds-surface p-4"
		>
			<ul className="space-y-3">
				{changeRequests.map((changeRequest) => (
					<ChangeRequestCard
						key={changeRequest.changeRequestId}
						changeRequest={changeRequest}
						tick="done"
						onToggleItem={(itemId, checked) =>
							toggleItem.mutate({ changeRequestId: changeRequest.changeRequestId, itemId, checked })
						}
						lockedReason={
							!canWork
								? REFUSAL.workOrder
								: currentStatus !== undefined && currentStatus !== changeRequest.sentBackTo
									? HANDED_OVER
									: undefined
						}
					/>
				))}
			</ul>
		</SectionCard>
	)
}
